import { HTMLProps, forwardRef } from "react";

import { cn } from "utils/helpers";

import { Button, ButtonProps } from "primereact/button";

import { kToolbarHeight } from "models";
import { Tooltip } from "utils/ui";
import { SliderInput } from "../SliderInput";
import { ColorPickerButton } from "../ColorPickerButton";

const SidebarWrapper = forwardRef<HTMLDivElement, SidebarProps>(
  ({ title, shouldShowContent = true, children, className, style, ...rest }, ref) => {
    const toolbarClassName = cn("w-full", className);

    return (
      shouldShowContent && (
        <div className={toolbarClassName} {...rest} ref={ref}>
          <div
            className="p-4 overflow-y-auto classy-scrollbar"
            style={style ?? { height: `calc(100vh - 60px - 60px - ${kToolbarHeight}px)` }}
          >
            {children}
          </div>
        </div>
      )
    );
  }
);

SidebarWrapper.displayName = "SidebarMenu.Wrapper";

const Heading = forwardRef<HTMLDivElement, SidebarProps>(({ className, title, value, ...rest }, ref) => {
  const style = {};
  const dividerClassName = cn("flex-1", className);

  return (
    <div className="flex flex-row mb-1">
      <h3 style={style} className={dividerClassName} ref={ref} {...rest}>
        {title}
      </h3>
      <span className="ml-2 text-sm" style={{ marginTop: "18px" }}>
        {value}
      </span>
    </div>
  );
});

Heading.displayName = "SidebarMenu.Heading";

/** 
 * A smaller, muted label for a group nested inside a `Heading` section (e.g.
 * the "Project" / "Company" chip groups under the "Fields" heading) — keeps
 * nested groups visually subordinate instead of matching the section title. 
 */
const SubHeading = forwardRef<HTMLDivElement, SidebarProps>(({ className, title, value, ...rest }, ref) => {
  const subHeadingClassName = cn(
    "flex-1 text-xs font-bold tracking-widest uppercase text-[var(--text-color-secondary)]",
    className
  );

  return (
    <div className="flex flex-row mb-2">
      <h4 className={subHeadingClassName} ref={ref} {...rest}>
        {title}
      </h4>
      {value !== undefined && <span className="ml-2 text-xs text-[var(--text-color-secondary)]">{value}</span>}
    </div>
  );
});

SubHeading.displayName = "SidebarMenu.SubHeading";

const Description = forwardRef<HTMLParagraphElement, HTMLProps<HTMLParagraphElement>>(
  ({ className, children, ...rest }, ref) => {
    const descriptionClassName = cn("text-sm text-[var(--text-color-secondary)]", className);

    return (
      <p className={descriptionClassName} ref={ref} {...rest}>
        {children}
      </p>
    );
  }
);

Description.displayName = "SidebarMenu.Description";

const Divider = forwardRef<HTMLDivElement, SidebarProps>(({ className, ...rest }, ref) => {
  const style = { minWidth: "1.5rem", height: "1px" };
  const dividerClassName = cn("bg-neutral-200 mt-4 mb-4 w-full my-1", className);

  return <div style={style} className={dividerClassName} ref={ref} {...rest} />;
});

Divider.displayName = "SidebarMenu.Divider";

const SidebarButton = forwardRef<HTMLDivElement, SidebarButtonProps | any>(
  ({ className, active, tooltip, tooltipShortcut, ...rest }, ref) => {
    const dividerClassName = cn("p-button-info", className);

    return (
      <Tooltip title={tooltip} shortcut={tooltipShortcut} offset={-50}>
        <Button outlined={!active} className={dividerClassName} {...rest}>
          {rest.children}
        </Button>
      </Tooltip>
    );
  }
);

SidebarButton.displayName = "SidebarMenu.Button";

type SidebarProps = {
  title?: string;
  value?: number | string;
  shouldShowContent?: boolean;
} & HTMLProps<HTMLDivElement>;

type SidebarButtonProps = {
  title?: string;
  tooltip?: string;
  tooltipShortcut?: string[];
  onClick?: () => void;
  active?: boolean;
} & ButtonProps &
  HTMLProps<HTMLDivElement>;

export const SidebarMenu = {
  Wrapper: SidebarWrapper,
  Divider: Divider,
  Heading: Heading,
  SubHeading: SubHeading,
  Description: Description,
  Button: SidebarButton,
  SliderInput: SliderInput,
  ColorPicker: ColorPickerButton,
};
